import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { Warranty } from '@wsphere/warranties/domain';
import { Subscription, switchMap } from 'rxjs';
import { WarrantyWebService } from '../../../warranty';
import { ClaimsTableComponent } from '../../components';
import { OpenClaimDialog } from '../../dialogs';
import {
  isWarrantyIneligible,
  warrantyIneligibleIcon,
  warrantyIneligibleMessage,
  warrantyIneligibleTitle,
} from '../../utils';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claims-page',
  templateUrl: './claims.page.html',
  standalone: false,
})
export class ClaimsPage implements OnInit, OnDestroy {
  private lastIneligibleToastAt = 0;

  constructor(
    private securitySvc: SecurityService,
    private configSvc: PortalService,
    private route: ActivatedRoute,
    private warrantySvc: WarrantyWebService,
    private toastSvc: ToastService
  ) {}
  @ViewChild('openClaimDialog') openClaimDialog?: OpenClaimDialog;
  @ViewChild('table') table?: ClaimsTableComponent;

  tenantName: string | null = null;
  selectedWarranty: Warranty.Model | null = null;
  private queryParamSub?: Subscription;

  canOpenClaim$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(async distributors => {
      const permissionKey = distributors?.map(dist => dist.permissionKey) || null;
      return await this.securitySvc.hasAccess('clm_OpenClaim', permissionKey);
    })
  );

  readonly isWarrantyIneligible = isWarrantyIneligible;
  readonly warrantyIneligibleMessage = warrantyIneligibleMessage;
  readonly warrantyIneligibleIcon = warrantyIneligibleIcon;

  async openClaim() {
    if (isWarrantyIneligible(this.selectedWarranty)) {
      this.showWarrantyIneligibleMessage(this.selectedWarranty);
      return;
    }

    if (!this.selectedWarranty) this.openClaimDialog?.openDialog();
    else this.openClaimDialog?.openDialog(this.selectedWarranty, this.selectedWarranty.customer ?? null);
  }

  showWarrantyIneligibleMessage(warranty: Warranty.Model | null) {
    const now = Date.now();
    if (now - this.lastIneligibleToastAt < 1000) return;
    this.lastIneligibleToastAt = now;

    this.toastSvc.show({
      type: 'info',
      title: warrantyIneligibleTitle(warranty),
      text: warrantyIneligibleMessage(warranty),
    });
  }

  private async setSelectedWarranty(warrantyId: string | null) {
    if (!warrantyId) {
      this.selectedWarranty = null;
      return;
    }

    try {
      this.selectedWarranty = await this.warrantySvc.getOneByWarrantyId(warrantyId);
    } catch {
      this.selectedWarranty = null;
    }
  }

  async ngOnInit() {
    const config = this.configSvc.config;
    this.tenantName = config.brandName;
    this.queryParamSub = this.route.queryParamMap.subscribe(params => {
      const warrantyId = params.get('warrantyId');
      void this.setSelectedWarranty(warrantyId);
    });
  }

  ngOnDestroy() {
    this.queryParamSub?.unsubscribe();
  }
}
